import { useState, useEffect } from "react";
import { adminAPI } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import Modal from "../../components/Modal/Modal";
import { useToast } from "../../hooks/useToast";
import "../Apps/Apps.css";
import { errorIcon } from "../../../utils/errorIcon";

interface Admin {
  _id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  invitedBy?: { name: string } | string;
  invite?: {
    isUsed: boolean;
    expiresAt: string;
  };
}

const AdminsList = () => {
  const { showToast } = useToast();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [pendingInvites, setPendingInvites] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "" });
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [adminsRes, invitesRes] = await Promise.all([
        adminAPI.getAllAdmins(),
        adminAPI.getPendingInvitations(),
      ]);

      const adminsData = adminsRes.data.data || adminsRes.data || [];
      setAdmins(Array.isArray(adminsData) ? adminsData : []);

      const invitesData = invitesRes.data.data || invitesRes.data || [];
      setPendingInvites(Array.isArray(invitesData) ? invitesData : []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      showToast({
        type: "warning",
        title: "Incomplete form",
        message: "Please fill in all fields",
      });
      return;
    }

    try {
      setIsInviting(true);
      await adminAPI.inviteAdmin(inviteForm);

      showToast({
        type: "success",
        title: "Admin invited successfully",
        message:
          "Admin invited successfully! They will receive an email with login credentials.",
      });

      setShowInviteModal(false);
      setInviteForm({ name: "", email: "" });
      await fetchData();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Failed to invite admin",
        message: error.response?.data?.message || "Failed to invite admin",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = async (email: string) => {
    try {
      await adminAPI.resendInvitation(email);
      showToast({
        type: "success",
        title: "Invitation resent",
        message: "Invitation resent successfully!",
      });
      await fetchData();
    } catch (error) {
      showToast({
        type: "error",
        title: "Failed to resend invitation",
        message: "Failed to resend invitation",
      });
    }
  };

  const handleCancelInvite = async (email: string) => {
    if (!window.confirm("Are you sure you want to cancel this invitation?"))
      return;

    try {
      await adminAPI.cancelInvitation(email);
      showToast({
        type: "success",
        title: "Invitation cancelled successfully",
        message: "Invitation cancelled successfully",
      });
      await fetchData();
    } catch (error) {
      showToast({
        type: "error",
        title: "Invitation failed to cancel",
        message: "Failed to cancel invitation",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;

    try {
      await adminAPI.deleteAdmin(id);
      await fetchData();

      showToast({
        type: "success",
        title: "Delete successfully",
        message: "Admin Deleted succesfully",
      });
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Delete faild",
        message: error.response?.data?.message || "Failed to delete admin",
      });
    }
  };

  const adminColumns = [
    {
      key: "name",
      label: "Admin",
      render: (admin: Admin) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            {admin.name}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {admin.email}
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Role",
      render: (admin: Admin) => (
        <span
          style={{
            textTransform: "capitalize",
            fontWeight: 600,
            color:
              admin.role === "super_admin"
                ? "var(--lico-orange)"
                : "var(--text-primary)",
          }}
        >
          {admin.role.replace("_", " ")}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Joined",
      render: (admin: Admin) => (
        <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
          {new Date(admin.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  const inviteColumns = [
    {
      key: "name",
      label: "Invitee",
      render: (invite: Admin) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            {invite.name}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {invite.email}
          </div>
        </div>
      ),
    },
    {
      key: "expiresAt",
      label: "Expires",
      render: (invite: Admin) => (
        <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
          {new Date(invite.invite?.expiresAt || "").toLocaleDateString()}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (invite: Admin) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={() => handleResendInvite(invite.email)}
            className="btn-icon"
            title="Resend Invitation"
          >
            📧
          </button>
          <button
            onClick={() => handleCancelInvite(invite.email)}
            className="btn-icon delete"
            title="Cancel Invitation"
          >
            {errorIcon()}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admins-page">
      <div className="page-header">
        <div>
          <h1>Admins Management</h1>
          <p>Manage admin users and invitations</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary"
        >
          <span>➕</span>
          <span>Invite Admin</span>
        </button>
      </div>

      {pendingInvites.length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "var(--text-primary)",
              marginBottom: "1rem",
            }}
          >
            Pending Invitations ({pendingInvites.length})
          </h2>
          <DataTable
            data={pendingInvites}
            columns={inviteColumns}
            isLoading={false}
            emptyMessage="No pending invitations"
            showActions={false}
          />
        </div>
      )}

      <div>
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            marginBottom: "1rem",
          }}
        >
          Active Admins ({admins.length})
        </h2>
        <DataTable
          data={admins}
          columns={adminColumns}
          isLoading={isLoading}
          onDelete={handleDelete}
          emptyMessage="No admins found"
          showEditButton={false}
        />
      </div>

      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setInviteForm({ name: "", email: "" });
          }}
          title="Invite New Admin"
        >
          <form
            onSubmit={handleInvite}
            style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
          >
            <div className="form-group">
              <label
                htmlFor="name"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                value={inviteForm.name}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, name: e.target.value })
                }
                placeholder="Admin Name"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  background: "var(--bg-secondary)",
                  border: "2px solid var(--border)",
                  borderRadius: "8px",
                  color: "var(--text-primary)",
                  fontSize: "0.95rem",
                }}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="email"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                }}
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={inviteForm.email}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, email: e.target.value })
                }
                placeholder="admin@lico.com"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  background: "var(--bg-secondary)",
                  border: "2px solid var(--border)",
                  borderRadius: "8px",
                  color: "var(--text-primary)",
                  fontSize: "0.95rem",
                }}
                required
              />
            </div>

            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--text-secondary)",
                padding: "0.75rem",
                background: "rgba(255, 153, 51, 0.1)",
                border: "1px solid rgba(255, 153, 51, 0.2)",
                borderRadius: "8px",
              }}
            >
              💡 The invited admin will receive an email with temporary login
              credentials.
            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "0.5rem",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteForm({ name: "", email: "" });
                }}
                className="modal-button secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isInviting}
                className="modal-button primary"
              >
                {isInviting ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AdminsList;
